import React, { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { ImageUpload } from "../../components/ImageUpload";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", description: "", image: "", order: 0, active: true });

  const load = () => api.get("/categories?active_only=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const start = (cat) => {
    setEditing(cat);
    setForm(cat ? { ...cat } : { name: "", slug: "", description: "", image: "", order: items.length, active: true });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/categories/${editing.slug}`, form);
      else await api.post("/admin/categories", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/admin/categories/${slug}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-display text-4xl">Categories</h1>
        <Button onClick={() => start(null)} className="rounded-full bg-primary gap-2" data-testid="add-category-btn"><Plus className="w-4 h-4" /> Add Category</Button>
      </div>
      <div className="bg-background rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left text-xs uppercase tracking-widest">
            <tr><th className="p-3">Name</th><th className="p-3">Slug</th><th className="p-3">Order</th><th className="p-3">Active</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.slug} className="border-t border-border" data-testid={`cat-row-${c.slug}`}>
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 text-muted-foreground">{c.slug}</td>
                <td className="p-3">{c.order}</td>
                <td className="p-3">{c.active ? "✓" : "✗"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => start(c)} className="p-1 hover:text-primary" data-testid={`edit-${c.slug}`}><Edit className="w-4 h-4 inline" /></button>
                  <button onClick={() => remove(c.slug)} className="p-1 hover:text-destructive ml-2" data-testid={`del-${c.slug}`}><Trash2 className="w-4 h-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">{editing ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" /></div>
            <div><Label>Slug (auto if empty)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="cat-slug" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <ImageUpload
              label="Banner Image"
              multiple={false}
              value={form.image}
              onChange={(v) => setForm({ ...form, image: v })}
              testId="cat-image-upload"
            />
            <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
            <Button type="submit" className="w-full rounded-full bg-primary" data-testid="cat-save">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
